/**
 * pmg-editorial-collections-list.js
 * Carosello + sticky hero per la sezione PMG Editorial Collections.
 * Nessuna dipendenza esterna, completamente isolato.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
   * STICKY HERO — HOIST
   * Sposta .pmg-sh-hero come primo figlio diretto di .pmg-ecl
   * così position:sticky funziona per tutta l'altezza della sezione.
   * ───────────────────────────────────────────────────────────── */
  function hoistHero(sectionEl) {
    const hero = sectionEl.querySelector('.pmg-sh-hero');
    if (!hero) return;
    if (hero.parentElement === sectionEl) return;
    sectionEl.prepend(hero);
  }

  /* ─────────────────────────────────────────────────────────────
   * HEADER WRAPPER — COLLASSO
   * Dopo il hoist, section-global__header lascia uno spazio bianco.
   * Se non ha contenuto visibile (o ha solo padding/margin dal tema),
   * lo collassiamo a 0 per eliminare il gap prima della griglia.
   * ───────────────────────────────────────────────────────────── */
  function collapseHeader(sectionEl) {
    const header = sectionEl.querySelector('.section-global__header, .editorial-header-block');
    if (!header) return;

    // Controlla se ci sono blocchi testo con contenuto reale
    const textBlock = header.querySelector('.section-header--block');
    const hasContent = textBlock && textBlock.children.length > 0;

    if (!hasContent) {
      header.style.cssText = 'height:0!important;padding:0!important;margin:0!important;overflow:hidden!important;min-height:0!important;';
    } else {
      // Ha contenuto: rimuovi solo i padding/margin extra del wrapper
      header.style.cssText = 'padding:0!important;margin:0!important;';
    }
  }

  /* ─────────────────────────────────────────────────────────────
   * STICKY HERO — CENTRATURA VERTICALE
   * top = headerHeight + (viewportAvailable - heroHeight) / 2
   * L'immagine risulta centrata nell'area visibile sotto l'header.
   * Scroll e dimensioni immagini restano invariati.
   *
   * CONSTRAINT: quando il fondo dell'immagine raggiungerebbe il
   * fondo della sezione, lo sticky si sblocca e il logo scrolls
   * via con il contenuto (evitando di coprire la sezione dopo).
   * ───────────────────────────────────────────────────────────── */
  function centerHero(sectionEl) {
    const hero = sectionEl.querySelector('.pmg-sh-hero');
    if (!hero) return;

    let cachedTopVal = 0;

    function computeTop() {
      const headerHeight  = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 0;
      const viewportAvail = window.innerHeight - headerHeight;
      const img           = hero.querySelector('img');
      const heroHeight    = img ? img.offsetHeight : 0;
      cachedTopVal        = Math.max(headerHeight, headerHeight + (viewportAvail - heroHeight) / 2);
      hero.style.top      = `${cachedTopVal}px`;
    }

    function constrainHero() {
      const img = hero.querySelector('img');
      if (!img) return;

      const heroImgHeight   = img.offsetHeight;
      const sectionRect     = sectionEl.getBoundingClientRect();
      const sectionBottom   = sectionRect.bottom;

      // Dove finirebbe il fondo dell'immagine se restasse sticky
      const heroImgBottom = cachedTopVal + heroImgHeight;

      if (sectionBottom <= heroImgBottom) {
        // Il fondo della sezione è sopra il fondo dell'immagine sticky:
        // sblocca lo sticky e posiziona il logo alla fine della sezione.
        hero.style.position = 'relative';
        hero.style.top      = `${sectionEl.scrollHeight - heroImgHeight}px`;
      } else {
        // Ripristina sticky
        hero.style.position = 'sticky';
        hero.style.top      = `${cachedTopVal}px`;
      }
    }

    computeTop();

    if (window.ResizeObserver) {
      new ResizeObserver(() => {
        computeTop();
        constrainHero();
      }).observe(hero);
    }
    window.addEventListener('resize', () => {
      computeTop();
      constrainHero();
    }, { passive: true });
    window.addEventListener('scroll', constrainHero, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────────
   * CAROSELLO
   * ───────────────────────────────────────────────────────────── */
  function initCarousel(carouselEl) {
    const track   = carouselEl.querySelector('.pmg-ecl__carousel-track');
    const slides  = carouselEl.querySelectorAll('.pmg-ecl__carousel-slide');
    const id      = carouselEl.id.replace('pmg-ecl-carousel-', '');
    const dots    = document.querySelectorAll(`[data-carousel-dots="${id}"] .pmg-ecl__carousel-dot`);
    const btnPrev = carouselEl.querySelector('[data-dir="prev"]');
    const btnNext = carouselEl.querySelector('[data-dir="next"]');

    if (!track || slides.length <= 1) return;

    let current = 0;
    const total = slides.length;

    function goTo(index) {
      current = ((index % total) + total) % total;
      track.style.transform = `translateX(-${current * 100}%)`;
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
    }

    if (btnPrev) btnPrev.addEventListener('click', () => goTo(current - 1));
    if (btnNext) btnNext.addEventListener('click', () => goTo(current + 1));

    dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

    let startX = null;
    carouselEl.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    carouselEl.addEventListener('touchend', e => {
      if (startX === null) return;
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) goTo(diff > 0 ? current + 1 : current - 1);
      startX = null;
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────────
   * INIT
   * ───────────────────────────────────────────────────────────── */
  function init() {
    document.querySelectorAll('.pmg-ecl').forEach(sectionEl => {
      hoistHero(sectionEl);
      collapseHeader(sectionEl);
      const hero = sectionEl.querySelector('.pmg-sh-hero');
      if (hero) {
        const img = hero.querySelector('img');
        if (img && !img.complete) {
          img.addEventListener('load', () => centerHero(sectionEl), { once: true });
        } else {
          centerHero(sectionEl);
        }
      }
    });
    document.querySelectorAll('[id^="pmg-ecl-carousel-"]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify Customizer hot-reload */
  document.addEventListener('shopify:section:load', e => {
    const sectionEl = e.target.querySelector('.pmg-ecl') || e.target;
    hoistHero(sectionEl);
    collapseHeader(sectionEl);
    centerHero(sectionEl);
    e.target.querySelectorAll('[id^="pmg-ecl-carousel-"]').forEach(initCarousel);
  });

})();
